import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LoadingSpinner from './LoadingSpinner';
import { useDispatch } from 'react-redux';
import { setUserDocuments } from '../reducers/documentsReducer';
import { fetchDocuments } from '../services/apiService';

const DocumentTable = ({ userDocuments, handleSelectedRow, handleShowDocumentUpload }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [searchTerm, setSearchTerm] = useState('');
  const [filteredDocuments, setFilteredDocuments] = useState(userDocuments);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const fetchUserDocuments = async () => {
    setIsLoading(true);
    try {
      const response = await fetchDocuments();
      if (response?.documents) {
        dispatch(setUserDocuments(response.documents));
        setFilteredDocuments(response.documents);
      } else {
        setFilteredDocuments([]);
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      setFilteredDocuments([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserDocuments();
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (Array.isArray(userDocuments)) {
        const filtered = userDocuments.filter((doc) =>
          doc.fileName.toLowerCase().includes(searchTerm.toLowerCase()),
        );
        setFilteredDocuments(filtered);
      }
    }, 200);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, userDocuments]);

  const handleRowClick = (doc) => {
    const isSame = selectedDocument?.id === doc.id;
    setSelectedDocument(isSame ? null : doc);
    handleSelectedRow(isSame ? null : doc);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={isLoading}
        />
        <Tooltip title="Upload new document">
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={handleShowDocumentUpload}
            disabled={isLoading}
          >
            Document
          </Button>
        </Tooltip>
      </Box>

      <TableContainer
        sx={{
          maxHeight: 400,
          overflowY: 'auto',
          minHeight: 300,
          border: '1px solid',
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
        }}
      >
        {isLoading ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{ mt: 10, height: '100%' }}
          >
            <LoadingSpinner size={30} />
            <Typography variant="body1" color="textSecondary">
              Loading...
            </Typography>
          </Box>
        ) : (
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', fontSize: 16 }}>Document</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: 16 }}>Last Uploaded</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: 16 }}>Indexed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(filteredDocuments) &&
                filteredDocuments.map((doc, index) => (
                  <TableRow
                    key={index}
                    hover
                    onClick={() => handleRowClick(doc)}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor:
                        selectedDocument?.id === doc.id
                          ? theme.palette.action.selected
                          : index % 2 === 0
                            ? theme.palette.action.hover
                            : 'inherit',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <TableCell>{doc.fileName}</TableCell>
                    <TableCell>{doc.uploadDate}</TableCell>
                    <TableCell>
                      {doc.isIndexed ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <CircularProgress size={20} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

export default DocumentTable;
